import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { config, useSpring } from "@react-spring/core";
import { animated } from "@react-spring/web";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import NextImage from "next/image";
import { useRouter } from "next/router";
import Link from "next/link";
import useIntersectionObserver from "@react-hook/intersection-observer";
import styled, { keyframes } from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Autoplay } from "swiper";
import "swiper/swiper.min.css";
import PageLayout from "lib/components/PageLayout";
import Head from "lib/components/Head";
import StickerGroup from "lib/assets/sticker-group.svg";
import StickerGroupSm from "lib/assets/sticker-group-sm.svg";
import IconStar from "lib/assets/rating-star-icon.svg";
import IconArrowRight from "lib/assets/arrow-right.svg";
import { useTheme } from "lib/contexts/themeContext";
import CommonService from "domain/common/commonService";
import CommunityService from "domain/community/communityService";
import Stack from "lib/components/Stack";
import Rating from "lib/components/Rating";
import Statistic from "lib/components/Statistic";
import useScript from "lib/hooks/useScript";
import rive1 from "lib/assets/rive/main-1.riv";
import rive2 from "lib/assets/rive/main-2.riv";
import rive3 from "lib/assets/rive/main-3.riv";
import rive4 from "lib/assets/rive/main-4.riv";
import riveImage3 from "lib/assets/rive/main-3.webp";
import riveImage4 from "lib/assets/rive/main-4.webp";
import Profile1 from "lib/assets/profile-sdo.png";
import Profile2 from "lib/assets/profile-mar.png";
import Profile3 from "lib/assets/profile-mal.png";
import Profile4 from "lib/assets/profile-kah.png";
import Profile5 from "lib/assets/profile-bra.png";
import Profile6 from "lib/assets/profile-eli.png";
import Profile7 from "lib/assets/profile-ghi.png";
import Profile8 from "lib/assets/profile-cra.png";
import Profile9 from "lib/assets/profile-ant.png";
import Profile10 from "lib/assets/profile-hda.png";
import caseTrackerImage from "lib/assets/landing-page/case-tracker.png";
import lawfullyProImage from "lib/assets/landing-page/lawfully-pro.png";
import useIsResMobile from "lib/hooks/useIsResMobile";
import ActionCloud from "lib/components/ActionCloud";
import ResourceLink from "lib/components/ResourceLink";
import { UIEvent } from "lib/enums";
import { useEvent } from "lib/contexts/eventContext";
import PromotionsService from "domain/promotions/promotionsService";
import TrustpilotService from "domain/reviews/trustpilotService";
import TrustpilotSection from "lib/components/TrustpilotSection";
import { PromotionBanner } from "lib/components/PromotionBanner";
import { getAirbridgeTrackerLink } from "lib/airbridge";
import { isMobile } from "react-device-detect";
import AttorneyTestimonials from "lib/components/AttorneyTestimonial";
import ButtonDownloadApp from "lib/components/ButtonDownloadApp";
import ModalDownloadApp from "lib/components/ModalDownloadApp";
import QRAppDownloadHub from "lib/assets/qr-app-download-hub.svg";
import { getIntlLocale, useLocale, useTranslations } from "lib/i18n";
import { PersonIcon } from "lib/assets/landing-page/person-icon";
import { BuildingIcon } from "lib/assets/landing-page/building-icon";

SwiperCore.use([Autoplay]);

const TESTIMONIALS = [
  {
    name: "sdo*****",
    contentKey: "reviews.sdo",
    color: "#f2f9ff",
    image: Profile1,
  },
  {
    name: "mar*******",
    contentKey: "reviews.mar",
    color: "#f7f1ff",
    image: Profile2,
  },
  {
    name: "Mal*****",
    contentKey: "reviews.mal",
    color: "#eff4ff",
    image: Profile3,
  },
  {
    name: "kah**********",
    contentKey: "reviews.kah",
    color: "#faf6ff",
    image: Profile4,
  },
  {
    name: "Bra*****",
    contentKey: "reviews.bra",
    color: "#f7f1ff",
    image: Profile5,
  },
  {
    name: "Eli*****",
    contentKey: "reviews.eli",
    color: "#f2f9fF",
    image: Profile6,
  },
  {
    name: "ghi*******",
    contentKey: "reviews.ghi",
    color: "#eff4ff",
    image: Profile7,
  },
  {
    name: "Cra*****",
    contentKey: "reviews.cra",
    color: "#faf6ff",
    image: Profile8,
  },
  {
    name: "Ant************",
    contentKey: "reviews.ant",
    color: "#f7f1ff",
    image: Profile9,
  },
  {
    name: "Hda************",
    contentKey: "reviews.hda",
    color: "#f2f9ff",
    image: Profile10,
  },
];

const ViewMain = ({
  banners,
  stats,
  trustpilot,
}): InferGetStaticPropsType<typeof getStaticProps> => {
  return (
    <PageLayout
      customCompMobile={
        <ButtonDownloadApp event={UIEvent.HubMobileTopAppDownloadClicked} />
      }
    >
      {/* PageLayout's MetaDefault supplies the title and description but no
          url, so the home page had no canonical. It needs one now that /es is
          advertised as an alternate -- each locale must declare its own URL. */}
      <Head url="/" />
      <div
        css={`
          overflow: hidden;
        `}
      >
        <Hero />
        <TrustpilotSection
          stats={trustpilot.stats}
          reviews={trustpilot.reviews}
        />
        <Stats {...stats} />
        <Description />
        <Banners banners={banners} />
        <Features nCases={stats.numCases} />
        <Testimonials rating={stats.appRating} />
        <TestimonialsAttorney />
        <Community />
        <Resources />
        <TooltipOpenBEP />
      </div>
    </PageLayout>
  );
};

export default ViewMain;

const Hero = () => {
  const { breakpoints } = useTheme();
  const t = useTranslations("home");
  const { track } = useEvent();
  const router = useRouter();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOnDownloadApp = useCallback(() => {
    if (isMobile) {
      window.open(
        getAirbridgeTrackerLink(true, "Hub", "Hero Section"),
        "_blank",
      );
      track(UIEvent.HubHeroMobileAppDownload);
    } else {
      setIsModalOpen(true);
      track(UIEvent.HubHeroDesktopAppDownload);
    }
  }, [track]);

  const handleOnBusinessSolutions = useCallback(() => {
    router.push("/pro");
  }, [router]);

  const handleOnClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div
      css={`
        padding: 120px 20px 100px;
        max-width: 1200px;
        margin: 0 auto;
        ${breakpoints.down(960)} {
          padding: 80px 20px 60px;
        }
      `}
    >
      {/* Header Section */}
      <div
        css={`
          text-align: center;
          margin-bottom: 65px;
          ${breakpoints.down(960)} {
            margin-bottom: 50px;
            padding: 0 20px;
          }
        `}
      >
        <h1
          css={`
            margin-top: 0;
            margin-left: auto;
            margin-right: auto;
            margin-bottom: 40px;
            font-size: 70px;
            font-weight: 700;
            line-height: 96px;
            color: #000;
            ${breakpoints.down(960)} {
              font-size: 40px;
              font-weight: 700;
              line-height: 56px;
              margin-bottom: 30px;
            }
          `}
        >
          {t("hero.title")}
        </h1>
        <p
          css={`
            margin: 20px 0 0 0;
            font-size: 24px;
            font-weight: 500;
            line-height: 34px;
            color: #0b0b0b;
            ${breakpoints.down(960)} {
              font-size: 16px;
              font-weight: 500;
              line-height: 24px;
            }
          `}
        >
          {t("hero.subtitle")}
        </p>
      </div>

      {/* Cards Section */}
      <div
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          height: fit-content;
          ${breakpoints.down(960)} {
            grid-template-columns: 1fr;
            gap: 30px;
          }
        `}
      >
        {/* Case Tracker Card */}
        <div
          css={`
            display: flex;
            flex-direction: column;
            background: #f5f3ff;
            border: 1px solid #e2e8f0;
            border-radius: 24px;
            padding: 32px;
            position: relative;
            ${breakpoints.down(960)} {
              padding: 20px;
            }
          `}
        >
          <div
            css={`
              margin-bottom: 16px;
            `}
          >
            <div
              css={`
                width: 48px;
                height: 48px;
                background: #ddd6fe;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <PersonIcon />
            </div>
            <h3
              css={`
                font-size: 24px;
                font-weight: 700;
                line-height: 36px;
                margin: 16px 0;
                color: #000000;
                ${breakpoints.down(960)} {
                  font-size: 24px;
                  line-height: 32px;
                }
              `}
            >
              {t("hero.caseTracker.title")}
            </h3>
            <p
              css={`
                font-size: 18px;
                font-weight: 400;
                line-height: 24px;
                color: #18181b;
                margin: 0;
              `}
            >
              {t("hero.caseTracker.description")}
            </p>
          </div>

          <div
            css={`
              height: 200px;
              margin-top: auto;
              margin-bottom: 24px;
              ${breakpoints.down(960)} {
                display: none;
                height: 160px;
              }
            `}
          >
            <img
              src={caseTrackerImage}
              alt={t("hero.caseTracker.imageAlt")}
              css={`
                width: 100%;
                border-radius: 12px;
              `}
            />
          </div>

          <button
            css={`
              background: #000;
              color: white;
              border: none;
              border-radius: 12px;
              padding: 16px 24px;
              font-size: 16px;
              font-weight: 600;
              line-height: 24px;
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: pointer;
              transition: all 0.2s ease;
              width: 100%;
              justify-content: center;

              &:hover {
                background: #1a1a1a;
              }
            `}
            onClick={handleOnDownloadApp}
          >
            {t("hero.caseTracker.cta")}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3.33337 8.00049H12.6667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 3.33398L12.6667 8.00065L8 12.6673"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Lawfully Pro For Business Card */}
        <div
          css={`
            display: flex;
            flex-direction: column;
            background: #f0f9ff;
            border: 1px solid #e2e8f0;
            border-radius: 24px;
            padding: 32px;
            position: relative;
            overflow: hidden;
            ${breakpoints.down(960)} {
              padding: 20px;
            }
          `}
        >
          <div
            css={`
              margin-bottom: 16px;
            `}
          >
            <div
              css={`
                width: 48px;
                height: 48px;
                background: #dbeafe;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <BuildingIcon />
            </div>
            <h3
              css={`
                margin: 16px 0;
                font-size: 24px;
                font-weight: 700;
                line-height: 36px;
                color: #000000;
                ${breakpoints.down(960)} {
                  font-size: 24px;
                  line-height: 32px;
                }
              `}
            >
              {t("hero.pro.title")}
            </h3>
            <p
              css={`
                font-size: 18px;
                font-weight: 400;
                line-height: 24px;
                color: #18181b;
                margin: 0;
              `}
            >
              {t("hero.pro.description")}
            </p>
          </div>

          <div
            css={`
              height: 200px;
              margin-top: auto;
              margin-bottom: 24px;
              overflow: hidden;
              ${breakpoints.down(960)} {
                display: none;
                height: 160px;
              }
            `}
          >
            <img
              src={lawfullyProImage}
              alt={t("hero.pro.imageAlt")}
              css={`
                width: 100%;
                border-radius: 12px;
              `}
            />
          </div>

          <button
            css={`
              background: #000;
              color: white;
              border: none;
              border-radius: 12px;
              padding: 16px 24px;
              font-size: 16px;
              font-weight: 600;
              line-height: 24px;
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: pointer;
              transition: all 0.2s ease;
              width: 100%;
              justify-content: center;

              &:hover {
                background: #1a1a1a;
              }
            `}
            onClick={handleOnBusinessSolutions}
          >
            {t("hero.pro.cta")}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3.33337 8.00049H12.6667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 3.33398L12.6667 8.00065L8 12.6673"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <ModalDownloadApp
        qr={QRAppDownloadHub}
        open={isModalOpen}
        onClose={handleOnClose}
      />
    </div>
  );
};

interface StatsProps {
  numCases: number;
  numPosts: number;
  appRating: number;
}

const Stats: React.FC<StatsProps> = ({ numCases, numPosts, appRating }) => {
  const { breakpoints } = useTheme();
  const t = useTranslations("home");
  const locale = useLocale();
  const container = useRef<HTMLDivElement>(null);
  const done = useRef(false);
  const { isIntersecting } = useIntersectionObserver(container, {
    threshold: 0.3,
  });
  const [styles, api] = useSpring(() => ({
    nCases: 0,
    rating: 0,
    nPosts: 0,
    config: config.molasses,
  }));

  useEffect(() => {
    if (!done.current && isIntersecting) {
      done.current = true;
      api.start({
        nCases: numCases,
        rating: appRating,
        nPosts: numPosts,
      });
    }
  }, [api, isIntersecting, appRating, numCases, numPosts]);

  return (
    <div
      css={`
        margin: 0 auto;
        padding: 120px 0;
        background-color: #f3f4f8;
        ${breakpoints.down("sm")} {
          padding: 80px 0;
        }
      `}
      ref={container}
    >
      <div
        css={`
          margin: 0 auto;
          max-width: 1020px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          ${breakpoints.down("sm")} {
            grid-template-columns: 1fr;
          }
        `}
      >
        <Statistic
          title={t("stats.registeredCases")}
          suffix="+"
          value={
            // eslint-disable-next-line
            // @ts-ignore
            <animated.span>
              {styles.nCases.to((n) =>
                new Intl.NumberFormat(getIntlLocale(locale)).format(
                  Math.round(n),
                ),
              )}
            </animated.span>
          }
        />
        <Statistic
          title={t("stats.communityPosts")}
          suffix="+"
          value={
            <animated.span>
              {styles.nPosts.to((n) =>
                new Intl.NumberFormat(getIntlLocale(locale)).format(
                  Math.round(n),
                ),
              )}
            </animated.span>
          }
        />
        <Statistic
          title={t("stats.appRating")}
          suffix={
            <IconStar
              style={{ marginLeft: 6 }}
              width={36}
              height={36}
              viewBox="0 0 36 33.75"
            />
          }
          value={
            <animated.span>
              {styles.rating.to((n) =>
                new Intl.NumberFormat(getIntlLocale(locale)).format(
                  Number(n.toFixed(1)),
                ),
              )}
            </animated.span>
          }
        />
      </div>
    </div>
  );
};

const Description = () => {
  const { breakpoints } = useTheme();
  const t = useTranslations("home");

  return (
    <div
      css={`
        margin-bottom: 144px;
        padding: 224px 0;
        background-color: #849cff;
        text-align: center;
        ${breakpoints.down("sm")} {
          margin-bottom: 100px;
          padding: 120px 0;
        }
      `}
    >
      <StickerGroup
        css={`
          margin: 0 auto;
          display: block;
          ${breakpoints.down("sm")} {
            display: none;
          }
        `}
      />
      <StickerGroupSm
        css={`
          margin: 0 auto;
          display: none;
          ${breakpoints.down("sm")} {
            display: block;
          }
        `}
      />
      <div
        css={`
          margin: 30px auto 0;
          max-width: 760px;
          line-height: 38px;
          font-size: 24px;
          font-weight: 500;
          color: white;
          ${breakpoints.down("sm")} {
            padding: 0 20px;
            line-height: 28px;
            font-size: 18px;
          }
        `}
      >
        {t("description")}
      </div>
    </div>
  );
};

interface BannersProps {
  banners: any[];
}

const Banners: React.FC<BannersProps> = ({ banners }) => {
  const { breakpoints } = useTheme();

  if (banners.length === 0) return null;

  return (
    <div
      css={`
        margin-bottom: 130px;
        ${breakpoints.down(576)} {
          margin-bottom: 100px;
        }
      `}
    >
      {/* sm view */}
      <Swiper
        css={`
          ${breakpoints.up(576)} {
            display: none;
          }
        `}
        autoplay={{ delay: 5000 }}
        slidesPerView={1}
        centeredSlides
      >
        {banners.map((banner, i) => {
          return (
            <SwiperSlide key={banner._id}>
              <div
                css={`
                  padding: 0 20px;
                `}
              >
                <PromotionBanner
                  banner={banner}
                  order={i + 1}
                  pagination={
                    banners.length > 1 ? `${i + 1} / ${banners.length}` : ""
                  }
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {/* lg view */}
      <Swiper
        css={`
          /* Add vertical padding so that mouse-over effect (shadow & translation) is visible. */
          .swiper-wrapper {
            padding: 20px 0;
          }
          .swiper-slide {
            width: auto;
          }
          ${breakpoints.down(576)} {
            display: none;
          }
        `}
        grabCursor
        autoplay={{ delay: 5000 }}
        slidesPerView="auto"
        spaceBetween={0}
      >
        {banners.map((banner, i) => {
          return (
            <SwiperSlide key={banner._id}>
              <div
                css={`
                  width: 320px;
                  margin-left: 20px;
                  ${i === 0 && "margin-left: calc(50vw - 500px);"}
                  ${i == banners.length - 1 &&
                  "margin-right: calc(50vw - 500px);"}
                ${breakpoints.down(1040)} {
                    ${i === 0 && "margin-left: 20px;"}
                    ${i == banners.length - 1 && "margin-right: 20px;"}
                  }
                  border-radius: 20px;
                  transition: all 0.15s ease-in-out;
                  :hover {
                    transform: translateY(-3px);
                    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
                  }
                `}
              >
                <PromotionBanner banner={banner} order={i + 1} />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

interface FeaturesProps {
  nCases: number;
}

const Features: React.FC<FeaturesProps> = ({ nCases }) => {
  const { breakpoints } = useTheme();
  const t = useTranslations("home");
  const locale = useLocale();
  const status = useScript("https://unpkg.com/rive-js@0.7.11/dist/rive.min.js");
  const isRiveReady = useMemo(() => status === "ready", [status]);

  return (
    <div
      css={`
        margin: 0 auto 80px;
        padding: 0 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
        ${breakpoints.down("sm")} {
          margin-bottom: 50px;
        }
      `}
    >
      <Feature
        event={UIEvent.HubSectionCaseTrackerClicked}
        href="/case-tracker"
        title={t("features.caseTracker.title")}
        content={t("features.caseTracker.content", {
          count: new Intl.NumberFormat(getIntlLocale(locale)).format(nCases),
        })}
        animationReady={isRiveReady}
        rive={rive1}
        width={380}
        height={496}
        shadow
      />
      <Feature
        event={UIEvent.HubSectionCaseAnalysisProClicked}
        href="/resources/start-case-analysis-pro"
        title={t("features.caseAnalysisPro.title")}
        content={t("features.caseAnalysisPro.content")}
        animationReady={isRiveReady}
        rive={rive2}
        width={380}
        height={366}
        reverse
        shadow
      />
      <Feature
        event={UIEvent.HubSectionCaseTrackerClicked}
        href="/lawyers"
        title={t("features.consultation.title")}
        content={t("features.consultation.content")}
        animationReady={isRiveReady}
        rive={rive3}
        image={riveImage3}
        width={400}
        height={458}
      />
      <Feature
        event={UIEvent.HubSectionCaseTrackerClicked}
        href="/mock-interview"
        title={t("features.mockInterview.title")}
        content={t("features.mockInterview.content")}
        animationReady={isRiveReady}
        rive={rive4}
        image={riveImage4}
        width={404}
        height={458}
        reverse
      />
    </div>
  );
};

interface FeatureProps {
  href: string;
  title: string;
  content: string;
  rive: any;
  animationReady: boolean;
  width: number;
  height: number;
  event: UIEvent;
  image?: string;
  reverse?: boolean;
  shadow?: boolean;
}

const Feature: React.FC<FeatureProps> = ({
  href,
  title,
  content,
  rive,
  animationReady,
  width,
  height,
  event,
  image,
  reverse,
  shadow,
}) => {
  const { track } = useEvent();
  const { breakpoints } = useTheme();
  const container = useRef<HTMLAnchorElement>(null);
  const t = useTranslations("home");
  const [cursorUrl, setCursorUrl] = useState("");
  const [isAnimPlaying, setIsAnimPlaying] = useState(false);
  const { isIntersecting } = useIntersectionObserver(container, {
    threshold: 0.5,
  });

  const handleOnClick = useCallback(() => {
    track(event);
  }, [event, track]);

  useEffect(() => {
    if (animationReady) {
      if (isIntersecting) {
        setIsAnimPlaying(true);
      } else {
        setIsAnimPlaying(false);
      }
    }
  }, [animationReady, isIntersecting, rive]);

  useEffect(() => {
    const svg = `<svg width="121" height="121" viewBox="0 0 121 121" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M54.5577 3.81456C58.1783 1.49035 62.8217 1.49036 66.4423 3.81456L74.337 8.88245C75.9288 9.90429 77.7572 10.4984 79.6457 10.6073L89.0115 11.1477C93.3067 11.3956 97.0633 14.1248 98.6263 18.1333L102.034 26.8737C102.722 28.636 103.852 30.1914 105.315 31.3895L112.575 37.3318C115.904 40.057 117.339 44.4731 116.247 48.6347L113.867 57.7091C113.387 59.5387 113.387 61.4613 113.867 63.2909L116.247 72.3653C117.339 76.5269 115.904 80.943 112.575 83.6682L105.315 89.6105C103.852 90.8086 102.722 92.364 102.034 94.1263L98.6263 102.867C97.0633 106.875 93.3067 109.604 89.0115 109.852L79.6457 110.393C77.7572 110.502 75.9288 111.096 74.337 112.118L66.4423 117.185C62.8217 119.51 58.1783 119.51 54.5577 117.185L46.663 112.118C45.0712 111.096 43.2428 110.502 41.3543 110.393L31.9885 109.852C27.6933 109.604 23.9367 106.875 22.3737 102.867L18.9656 94.1263C18.2784 92.364 17.1484 90.8086 15.6847 89.6105L8.42521 83.6682C5.09592 80.943 3.66106 76.5269 4.75268 72.3653L7.13293 63.2909C7.61286 61.4613 7.61286 59.5387 7.13293 57.7091L4.75268 48.6347C3.66106 44.4731 5.09593 40.057 8.42521 37.3318L15.6847 31.3895C17.1484 30.1914 18.2784 28.636 18.9656 26.8737L22.3737 18.1333C23.9367 14.1248 27.6933 11.3956 31.9886 11.1477L41.3543 10.6073C43.2428 10.4984 45.0712 9.90429 46.663 8.88245L54.5577 3.81456Z" fill="#33C3FF"/><text x="60.5" y="52" fill="white" font-family="sans-serif" font-size="17" font-weight="600" text-anchor="middle">${t(
      "features.cursorLine1",
    )}</text><text x="60.5" y="72" fill="white" font-family="sans-serif" font-size="17" font-weight="600" text-anchor="middle">${t(
      "features.cursorLine2",
    )}</text><path d="M60 76.9289L67.0711 84L60 91.0711" stroke="white" stroke-width="2"/><path d="M67 84L52 84" stroke="white" stroke-width="2"/></svg>`;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    setCursorUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [t]);

  return (
    <Link href={href}>
      <a
        css={`
          position: relative;
          max-width: 1000px;
          width: 100%;
          height: 700px;
          display: flex;
          flex-direction: ${reverse ? "row-reverse" : "row"};
          align-items: center;
          color: black;
          cursor: url(${cursorUrl}) 60.5 60.5, pointer;
          :hover {
            color: black;
          }
          ${breakpoints.down("sm")} {
            margin-bottom: 100px;
            height: auto;
            flex-direction: column;
          }
        `}
        ref={container}
        onClick={handleOnClick}
      >
        <div
          css={`
            flex: 1;
            margin-left: ${reverse ? 130 : 0}px;
            margin-right: ${reverse ? 0 : 130}px;
            ${breakpoints.down("sm")} {
              margin-left: 0;
              margin-right: 0;
              width: 100%;
            }
          `}
        >
          <div
            css={`
              margin-bottom: 30px;
              line-height: 56px;
              font-size: 40px;
              font-weight: 700;
              ${breakpoints.down("sm")} {
                line-height: 42px;
                font-size: 30px;
              }
            `}
          >
            {title}
          </div>
          <div
            css={`
              line-height: 26px;
              font-size: 18px;
              font-weight: 500;
              ${breakpoints.down("sm")} {
                margin-bottom: 20px;
                line-height: 24px;
                fotn-size: 16px;
              }
            `}
          >
            {content}
          </div>
        </div>
        <div
          css={`
            display: none;
            margin-bottom: 20px;
            width: 100%;
            ${breakpoints.down("sm")} {
              display: block;
            }
          `}
        >
          <Link href={{ pathname: href }} passHref>
            <div
              css={`
                display: flex;
                align-items: center;
                line-height: 24px;
                font-size: 16px;
                font-weight: 700;
                color: #00b4ff;
                :hover {
                  color: #00b4ff;
                }
              `}
              onClick={handleOnClick}
            >
              <span
                css={`
                  margin-right: 3px;
                `}
              >
                {t("features.learnMore")}
              </span>
              <IconArrowRight stroke="white" />
            </div>
          </Link>
        </div>
        <div
          css={`
            width: ${width}px;
            display: flex;
            ${breakpoints.down("sm")} {
              width: 100%;
              max-width: ${width}px;
            }
          `}
        >
          <div
            css={`
              width: 100%;
              height: 0;
              padding-bottom: ${(380 / 496) * 100}%;
              padding-bottom: ${(height / width) * 100}%;
              position: relative;
            `}
          >
            {!!image && (
              <div
                css={`
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                `}
              >
                <NextImage
                  unoptimized
                  priority
                  width={2 * width}
                  height={2 * height}
                  src={image}
                  alt=""
                />
              </div>
            )}
            <div
              css={`
                position: relative;
              `}
            >
              {isAnimPlaying && (
                <RiveCanvas
                  // Derived from href, not title: title is translated copy, so
                  // keying the DOM id off it would change the id per locale and
                  // let a copy edit break the animation lookup.
                  id={`riveCanvas${href.replace(/\W/g, "")}`}
                  src={rive}
                  width={width}
                  height={height}
                  shadow={shadow}
                />
              )}
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
};

interface RiveCanvasProps {
  id: string;
  src: any;
  width: number;
  height: number;
  shadow?: boolean;
}

const RiveCanvas: React.FC<RiveCanvasProps> = ({
  id,
  src,
  width,
  height,
  shadow,
}) => {
  useEffect(() => {
    new window.rive.Rive({
      src,
      canvas: document.getElementById(id),
      layout: new window.rive.Layout({ fit: "contain" }),
      autoplay: true,
    });
  }, [id, src]);

  return (
    <canvas
      css={`
        box-shadow: ${shadow ? "0px 4px 30px rgba(0, 0, 0, 0.1)" : "none"};
        border-radius: 20px;
      `}
      style={{ width: "100%" }}
      id={id}
      width={2 * width}
      height={2 * height}
    />
  );
};

const slideKeyframes = (width: number) => {
  const slide = keyframes`
    100% {
      transform: translateX(${-1 * width}px);
    }
  `;
  return slide;
};

interface TestimonialsProps {
  rating: number;
}

const Testimonials: React.FC<TestimonialsProps> = ({ rating }) => {
  const { breakpoints } = useTheme();
  const t = useTranslations("home");
  const isResMobile = useIsResMobile();

  return (
    <div
      css={`
        overflow: hidden;
        margin-bottom: 80px;
        ${breakpoints.down("sm")} {
          margin-bottom: 80px;
        }
      `}
    >
      <div
        css={`
          margin-bottom: 40px;
        `}
      >
        <div
          css={`
            margin-bottom: 60px;
            line-height: 56px;
            font-size: 40px;
            font-weight: 700;
            text-align: center;
            ${breakpoints.down("sm")} {
              line-height: 42px;
              font-size: 30px;
            }
          `}
        >
          {t("testimonials.title")}
        </div>
        <div
          css={`
            margin-bottom: 13px;
            display: flex;
            justify-content: center;
            align-items: center;
          `}
        >
          <Rating
            rating={rating}
            size={isResMobile ? 35 : 45}
            gutter={isResMobile ? 10 : 13}
            hideText
          />
          <span
            css={`
              margin-left: 14.3px;
              line-height: 56px;
              font-size: 40px;
              font-weight: 700;
              color: #325aff;
              ${breakpoints.down("sm")} {
                line-height: 42px;
                font-size: 30px;
              }
            `}
          >
            {rating}
          </span>
        </div>
        <div
          css={`
            line-height: 28px;
            font-size: 20px;
            font-weight: 500;
            text-align: center;
            color: #73789d;
            ${breakpoints.down("sm")} {
              line-height: 26px;
              font-size: 18px;
            }
          `}
        >
          {t("testimonials.source")}
        </div>
      </div>

      {/* Large view */}
      <InfiniteSlide
        css={`
          ${breakpoints.down("sm")} {
            display: none;
          }
        `}
        itemWidth={490}
      >
        {TESTIMONIALS.map((data, i) => (
          <Testimonial
            key={i}
            name={data.name}
            content={t(data.contentKey)}
            color={data.color}
            image={data.image}
          />
        ))}
      </InfiniteSlide>

      {/* Small view */}
      <div
        css={`
          display: none;
          ${breakpoints.down("sm")} {
            display: block;
          }
        `}
      >
        <InfiniteSlide
          css={`
            margin-bottom: 20px;
          `}
          itemWidth={300}
        >
          {TESTIMONIALS.slice(0, 5).map((data, i) => (
            <Testimonial
              key={i}
              name={data.name}
              content={t(data.contentKey)}
              color={data.color}
              image={data.image}
            />
          ))}
        </InfiniteSlide>
        <InfiniteSlide
          css={`
            margin-left: -200px;
          `}
          itemWidth={300}
        >
          {TESTIMONIALS.slice(5, 10).map((data, i) => (
            <Testimonial
              key={i}
              name={data.name}
              content={t(data.contentKey)}
              color={data.color}
              image={data.image}
            />
          ))}
        </InfiniteSlide>
      </div>
    </div>
  );
};

interface InfiniteSlideProps {
  className?: string;
  itemWidth: number;
}

const InfiniteSlide: React.FC<InfiniteSlideProps> = ({
  className,
  itemWidth,
  children,
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const count = React.Children.count(children);
  // We need to repeat the contents, with aria-hidden set to "true",
  // to give illusion of infinite stream.
  const duplicate = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement, { hidden: true });
    }
    return null;
  });

  const handleOnTouchStart = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleOnTouchEnd = useCallback(() => {
    setIsPaused(false);
  }, []);

  return (
    <div
      css={`
        overflow: hidden;
        user-select: none;
      `}
      className={className}
      onTouchStart={handleOnTouchStart}
      onTouchEnd={handleOnTouchEnd}
    >
      <div
        css={`
          display: grid;
          grid-template-columns: repeat(${2 * count}, ${itemWidth}px);
          column-gap: 20px;
          transform: translateZ(0);
          animation: ${slideKeyframes(count * (itemWidth + 20))}
            ${(count * (itemWidth + 20)) / 100}s linear infinite;
          animation-play-state: ${isPaused ? "paused" : "running"};
        `}
      >
        {children}
        {duplicate}
      </div>
    </div>
  );
};

interface TestimonialProps {
  color: string;
  name?: string;
  content?: string;
  image?: string;
  hidden?: boolean;
}

const Testimonial: React.FC<TestimonialProps> = ({
  color,
  name,
  content,
  image,
  hidden,
}) => {
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        border-radius: 20px;
        padding: 30px;
        max-width: 490px;
        height: 280px;
        background-color: ${color};
        ${breakpoints.down("sm")} {
          height: 380px;
        }
      `}
      aria-hidden={hidden}
    >
      <div
        css={`
          margin-bottom: 20px;
          display: flex;
          align-items: center;
        `}
      >
        <div
          css={`
            overflow: hidden;
            margin-right: 14px;
            border-radius: 22px;
            width: 44px;
            height: 44px;
            background: skyblue;
          `}
        >
          <img
            css={`
              border-radius: 22px;
              width: 100%;
              height: auto;
            `}
            src={image}
            alt=""
          />
        </div>
        <div
          css={`
            line-height: 28px;
            font-size: 20px;
            font-weight: 700;
          `}
        >
          {name}
        </div>
      </div>
      <div
        css={`
          line-height: 24px;
          font-size: 16px;
          white-space: pre-line;
        `}
      >
        {content}
      </div>
    </div>
  );
};

const Community = () => {
  const { breakpoints } = useTheme();
  const t = useTranslations("home");
  const router = useRouter();
  const { track } = useEvent();

  const handleOnClick = useCallback(
    (e) => {
      e.preventDefault();
      track(UIEvent.HubCTACommunityClicked);
      router.push("/community/categories/approved");
    },
    [router, track],
  );

  return (
    <div
      css={`
        margin: 0 auto 200px;
        ${breakpoints.down("sm")} {
          margin: 0 auto 160px;
        }
      `}
    >
      <ActionCloud
        text={t("community.prompt")}
        buttonText={t("community.cta")}
        color="#e2f7ff"
        buttonColor="#00b4ff"
        onClick={handleOnClick}
      />
    </div>
  );
};

const Resources = () => {
  const { breakpoints } = useTheme();
  const t = useTranslations("home");

  return (
    <div
      css={`
        margin: 0 auto 160px;
        padding: 0 20px;
        max-width: 1000px;
        ${breakpoints.down("sm")} {
          margin: 0 auto 100px;
        }
      `}
    >
      <SectionTitle
        css={`
          ${breakpoints.down("sm")} {
            line-height: 34px;
            font-size: 24px;
          }
        `}
      >
        {t("resources.title")}
      </SectionTitle>
      <Stack spacing="10px">
        <ResourceLink
          event={UIEvent.HubResourceLinkClicked}
          title="What can I expect at the marriage Green Card interview?"
          href="/resources/prepare-for-marriage-green-card-interview-and-common-questions#iii-what-can-i-expect-at-the-marriage-green-card-interview"
        />
        <ResourceLink
          event={UIEvent.HubResourceLinkClicked}
          title="Check how many filers you are ahead of or behind."
          href="/resources/start-case-analysis-pro"
        />
        <ResourceLink
          event={UIEvent.HubResourceLinkClicked}
          title="Naturalization Processing Time"
          href="/resources/naturalization-processing-time"
        />
        <ResourceLink
          event={UIEvent.HubResourceLinkClicked}
          title="Here's 5 Tips for Your Green Card Application"
          href="/resources/5-tips-for-green-card-application"
        />
        <ResourceLink
          event={UIEvent.HubResourceLinkClicked}
          title="You May Be Eligible for Reduced Fees and Fee Waiver"
          href="/resources/reduced-fees-and-fee-waiver"
        />
      </Stack>
    </div>
  );
};

const SectionTitle = styled.h2`
  margin-bottom: 60px;
  text-align: center;
  line-height: 56px;
  font-size: 40px;
  ${({ theme: { breakpoints } }) => breakpoints.down("sm")} {
    margin-bottom: 40px;
    line-height: 42px;
    font-size: 30px;
  }
`;

const TestimonialsAttorney: React.FC = () => {
  const t = useTranslations("home");
  return (
    <div
      css={`
        margin-bottom: 160px;
      `}
    >
      <SectionTitle
        css={`
          padding: 0 20px;
        `}
      >
        {t("attorneys.title")}
      </SectionTitle>
      <AttorneyTestimonials />
    </div>
  );
};

const bounceAnimation = keyframes`
 0% { transform: translateY(0px);}
 50% { transform: translateY(10px);}
 100% { transform: translateY(0px);}
`;

const TooltipOpenBEP: React.FC = () => {
  const { query } = useRouter();
  const t = useTranslations("home");
  const { openBEP } = query as {
    openBEP: string;
  };

  return openBEP === "true" ? (
    <div
      css={`
        background-color: #325aff;
        position: fixed;
        width: 264px;
        right: 30px;
        top: 15px;
        color: white;
        z-index: 9999;
        border-radius: 13px;
        padding: 26px;
        font-size: 20px;
        text-align: center;
        animation-name: ${bounceAnimation};
        animation-duration: 3s;
        animation-iteration-count: infinite;
        animation-timing-function: ease-in-out;

        &::before {
          width: 20px;
          height: 20px;
          background-color: #325aff;
          position: absolute;

          left: 132px;
          top: 0;
          transform: rotate(45deg) translateX(-50%);
          content: " ";
        }
      `}
    >
      {t("extensionTooltip")}
    </div>
  ) : null;
};

export const getStaticProps: GetStaticProps = async () => {
  const commonService = new CommonService();
  const communityService = new CommunityService();
  const promotionsService = new PromotionsService();
  const trustpilotService = new TrustpilotService();
  const [
    banners,
    { numCases },
    appRating,
    { totalCount: numPosts },
    trustpilotStats,
    trustpilotReviews,
  ] = await Promise.all([
    promotionsService.getListBanner(),
    commonService.getLawfullyStats(),
    commonService.getAppstoreRating(),
    communityService.getPublicListPost({ page_size: 1 }),
    trustpilotService.getStats(),
    trustpilotService.getRecentFiveStarReviews(3),
  ]);

  return {
    props: {
      banners,
      stats: {
        numCases,
        numPosts,
        appRating: Number(appRating.toFixed(1)),
      },
      trustpilot: {
        stats: trustpilotStats,
        reviews: trustpilotReviews,
      },
    },
    revalidate: process.env.NEXT_PUBLIC_NETLIFY ? 10 : 60, // 1 min
  };
};
